import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { Anchor, Box, Group, SimpleGrid, Stack, Text, Collapse, UnstyledButton } from '@mantine/core';
import { IdCard, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { formatTimestamp } from '@/shared/DateFormatting';
import ProviderIconComponent from '@/shared/ProviderIconComponent';
import DetailDrawer from '@/design/DetailDrawer';
import StatTile from '@/design/StatTile';
import KeyValueTable from '@/design/KeyValueTable';
import InitialAvatar from '@/design/InitialAvatar';
import type { DirectoryUserRecord } from './DirectoryApiService';
import DirectoryStatusBadge from './DirectoryStatusBadge';

interface DirectoryUserDrawerProps {
    user: DirectoryUserRecord | null;
    isOpen: boolean;
    sourceLabel: string;
    manager: DirectoryUserRecord | null;
    reports: DirectoryUserRecord[];
    onOpenUser: (user: DirectoryUserRecord) => void;
    onClose: () => void;
}

const labelStyle = { fontSize: '10px' } as const;

/**
 * Which provider-specific identity fields each directory provider can supply.
 * Keyed by the `providerTypeId` prefix. A supported field is always shown
 * (with an em dash when the individual record has no value); an unsupported
 * one is omitted entirely.
 */
const PROVIDER_OPTIONAL_FIELDS: Record<string, { manager: boolean; orgUnit: boolean }> = {
    'google-workspace': { manager: true, orgUnit: true },
    okta: { manager: true, orgUnit: false },
    'entra-id': { manager: false, orgUnit: false },
};

function DetailField({ label, children }: { label: string; children: ReactNode }) {
    return (
        <Box>
            <Text size="xs" c="dimmed" fw={700} mb={4} style={labelStyle}>
                {label}
            </Text>
            {children}
        </Box>
    );
}

/**
 * Common identity fields render for every provider; provider-specific fields
 * (Manager, Org Unit) appear per the source provider's capabilities. Manager
 * resolves to the manager's name (clickable) when that record is loaded.
 */
function IdentityDetails({
    user,
    sourceLabel,
    manager,
    onOpenUser,
}: {
    user: DirectoryUserRecord;
    sourceLabel: string;
    manager: DirectoryUserRecord | null;
    onOpenUser: (user: DirectoryUserRecord) => void;
}): ReactElement {
    const fields: { label: string; value: ReactNode }[] = [
        { label: 'Title', value: <Text size="xs">{user.title ?? '—'}</Text> },
        {
            label: 'Cost Center',
            value: (
                <Text size="xs" ff="monospace">
                    {user.costCenter ?? '—'}
                </Text>
            ),
        },
        {
            label: 'Source',
            value: (
                <Group gap="xs" wrap="nowrap">
                    <ProviderIconComponent providerTypeId={user.providerTypeId} size={14} />
                    <Text size="xs">{sourceLabel}</Text>
                </Group>
            ),
        },
        {
            label: 'External ID',
            value: (
                <Text size="xs" ff="monospace">
                    {user.externalId}
                </Text>
            ),
        },
    ];

    const support = PROVIDER_OPTIONAL_FIELDS[user.providerTypeId.split('/')[0] ?? ''] ?? { manager: false, orgUnit: false };
    if (support.manager) {
        const managerValue = manager ? (
            <Anchor component="button" type="button" size="xs" onClick={() => onOpenUser(manager)}>
                {manager.displayName || manager.email}
            </Anchor>
        ) : (
            <Text size="xs">{user.managerId ?? '—'}</Text>
        );
        fields.push({ label: 'Manager', value: managerValue });
    }
    if (support.orgUnit) fields.push({ label: 'Org Unit', value: <Text size="xs">{user.orgUnitPath ?? '—'}</Text> });

    return (
        <>
            <Group gap="xs" mb="sm">
                <IdCard size={14} />
                <Text size="xs" fw={700}>
                    Identity Details
                </Text>
            </Group>
            <SimpleGrid cols={2} spacing="sm" mb="sm">
                {fields.map((f) => (
                    <DetailField key={f.label} label={f.label}>
                        {f.value}
                    </DetailField>
                ))}
            </SimpleGrid>
        </>
    );
}

function DirectReports({
    reports,
    onOpenUser,
}: {
    reports: DirectoryUserRecord[];
    onOpenUser: (user: DirectoryUserRecord) => void;
}): ReactElement {
    return (
        <Box mt="md">
            <Group gap="xs" mb="sm">
                <Users size={14} />
                <Text size="xs" fw={700}>
                    Direct reports ({reports.length})
                </Text>
            </Group>
            <Stack gap={4}>
                {reports.map((r) => (
                    <UnstyledButton
                        key={`${r.connectionId}-${r.externalId}`}
                        onClick={() => onOpenUser(r)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '6px 8px',
                            borderRadius: 'var(--bm-radius)',
                            border: '1px solid var(--bm-color-border-subtle)',
                        }}
                    >
                        <InitialAvatar source={r.displayName || r.email} size={24} />
                        <Box>
                            <Text size="xs" fw={500}>
                                {r.displayName || r.email}
                            </Text>
                            <Text size="xs" c="dimmed">
                                {r.title ?? r.email}
                            </Text>
                        </Box>
                    </UnstyledButton>
                ))}
            </Stack>
        </Box>
    );
}

export default function DirectoryUserDrawerComponent({
    user,
    isOpen,
    sourceLabel,
    manager,
    reports,
    onOpenUser,
    onClose,
}: DirectoryUserDrawerProps): ReactElement {
    const [showAttributes, setShowAttributes] = useState(false);
    const attributeEntries = user?.attributes ? Object.entries(user.attributes) : [];

    useEffect(() => {
        setShowAttributes(false);
    }, [user?.externalId]);

    return (
        <DetailDrawer
            opened={isOpen && !!user}
            onClose={onClose}
            avatarSource={user ? user.displayName || user.email : ''}
            title={user?.displayName || '—'}
            subtitle={user?.email}
            stats={
                user && (
                    <>
                        <StatTile label="Status">
                            <DirectoryStatusBadge status={user.status} />
                        </StatTile>
                        <StatTile label="Department">
                            <Text size="xs" fw={700}>
                                {user.department ?? '—'}
                            </Text>
                        </StatTile>
                        <StatTile label="Synced">
                            <Text size="xs" fw={700}>
                                {formatTimestamp(user.syncedAt)}
                            </Text>
                        </StatTile>
                    </>
                )
            }
        >
            {user && (
                <>
                    <IdentityDetails user={user} sourceLabel={sourceLabel} manager={manager} onOpenUser={onOpenUser} />

                    {reports.length > 0 && <DirectReports reports={reports} onOpenUser={onOpenUser} />}

                    {attributeEntries.length > 0 && (
                        <Box mt="md">
                            <UnstyledButton
                                onClick={() => setShowAttributes((v) => !v)}
                                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}
                            >
                                <Text size="xs" fw={700}>
                                    Provider Attributes ({attributeEntries.length})
                                </Text>
                                {showAttributes ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </UnstyledButton>
                            <Collapse in={showAttributes}>
                                <KeyValueTable entries={attributeEntries} />
                            </Collapse>
                        </Box>
                    )}
                </>
            )}
        </DetailDrawer>
    );
}
